import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Download, Home, LayoutDashboard, LogOut, Receipt } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function UserOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/me/orders")
      .then(({ data }) => setOrders(data))
      .catch(() => toast.error("No se pudieron cargar los pedidos"))
      .finally(() => setLoading(false));
  }, []);

  const downloadInvoice = async (order) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/me/orders/${order.id}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "No se pudo descargar el justificante de pago");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `factura-${order.invoice_number || order.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const totalGasto = orders.reduce((acc, o) => acc + (Number(o.amount_eur) || 0), 0);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="user-orders-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="laclaseencasa" className="w-8 h-8 object-contain" />
            <span className="font-display text-lg text-brand-ink">laclaseencasa</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 hidden sm:inline">{user?.name}</span>
            <Link to="/dashboard" data-testid="orders-back-to-dashboard">
              <Button variant="ghost" size="sm">
                <LayoutDashboard className="w-4 h-4 mr-1" /> Mi panel
              </Button>
            </Link>
            <Link to="/" data-testid="orders-back-to-home">
              <Button variant="ghost" size="sm">
                <Home className="w-4 h-4 mr-1" /> Home
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="orders-logout"
            >
              <LogOut className="w-4 h-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl text-gray-900 flex items-center gap-3">
              <Receipt className="w-8 h-8 text-brand" />
              Mis pedidos
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              Historial de compras. Puedes volver a descargar el justificante de pago de cualquier pedido cuando lo necesites.
            </p>
          </div>
          {orders.length > 0 && (
            <Card className="p-5 border border-gray-200 shadow-none rounded-lg w-full md:w-72">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">Gasto acumulado</span>
                <span className="text-[10px] text-gray-400 font-mono-data">{orders.length} pedidos</span>
              </div>
              <p className="font-display text-3xl text-gray-900" data-testid="total-spent">
                {fmtEur(totalGasto)}
              </p>
            </Card>
          )}
        </div>

        {loading ? (
          <p className="text-gray-500">Cargando pedidos…</p>
        ) : orders.length === 0 ? (
          <Card
            className="p-10 border border-gray-200 shadow-none rounded-lg text-center"
            data-testid="orders-empty"
          >
            <Receipt className="w-14 h-14 mx-auto text-gray-300" />
            <h2 className="font-display text-2xl text-brand-ink mt-4">Aún no tienes pedidos</h2>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              Cuando compres un curso o un paquete, aparecerá aquí junto al justificante de pago descargable.
            </p>
            <Link to="/">
              <Button className="mt-5 bg-brand hover:bg-brand-hover text-white" data-testid="orders-empty-cta">
                Ver el catálogo
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <Card
                key={o.id}
                className="p-5 border border-gray-200 shadow-none rounded-lg"
                data-testid={`order-row-${o.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-mono-data">
                        Pedido
                      </span>
                      <span className="font-mono-data text-brand-ink" data-testid={`order-invoice-${o.id}`}>
                        {o.invoice_number || o.id.slice(-8)}
                      </span>
                      <span className="text-xs text-gray-400" data-testid={`order-date-${o.id}`}>
                        {fmtDate(o.paid_at || o.created_at)}
                      </span>
                    </div>
                    <ul className="mt-2 text-sm text-brand-ink space-y-0.5">
                      {o.courses.map((c) => (
                        <li key={c.id} className="flex items-start gap-2">
                          <span className="text-brand mt-0.5">·</span>
                          <span>{c.title || c.id}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center gap-4 md:flex-col md:items-end">
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-mono-data">
                        Importe
                      </p>
                      <p
                        className="font-display text-2xl text-brand-ink"
                        data-testid={`order-amount-${o.id}`}
                      >
                        {fmtEur(o.amount_eur)}
                      </p>
                    </div>
                    {o.has_invoice ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadInvoice(o)}
                        data-testid={`order-download-${o.id}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Justificante de pago
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400">Justificante no disponible</span>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
